// Message Content Component - Handles different message types

import React from 'react';
import { Info, TrendingUp, AlertTriangle, BarChart3, Lightbulb, Sparkles, GitCompare, CheckCircle2, Wrench } from 'lucide-react';
import { COLORS, CHART_COLORS } from '../../constants/theme';
import AreaChart from '../charts/AreaChart';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';
import ProductTable from '../common/ProductTable';
import Suggestions from './Suggestions';
import FollowupSuggestions from './FollowupSuggestions';
import KPICard from '../common/KPICard';
import { isValidUrl, extractUrl } from '../../utils/urlUtils';

// Strip AI-generated type prefix (e.g. "Recommendation: ", "Summary: ") from insight text
const stripTypePrefix = (text) => {
  return text.replace(/^(recommendation|summary|trend|outlier|comparison|improvement|insight)\s*:\s*/i, '');
};

// Friendly config: emoji icon, soft colour, human-readable heading
const INSIGHT_CONFIG = {
  summary: {
    icon: '📊', iconComp: Info,
    bg: 'bg-blue-50', border: 'border-blue-100', accent: 'bg-blue-500',
    labelBg: 'bg-blue-100', labelText: 'text-blue-700',
    heading: 'Key Takeaway',
  },
  recommendation: {
    icon: '💡', iconComp: Lightbulb,
    bg: 'bg-amber-50', border: 'border-amber-100', accent: 'bg-amber-400',
    labelBg: 'bg-amber-100', labelText: 'text-amber-700',
    heading: 'What to do next',
  },
  trend: {
    icon: '📈', iconComp: TrendingUp,
    bg: 'bg-purple-50', border: 'border-purple-100', accent: 'bg-purple-500',
    labelBg: 'bg-purple-100', labelText: 'text-purple-700',
    heading: 'Pattern spotted',
  },
  comparison: {
    icon: '⚖️', iconComp: GitCompare,
    bg: 'bg-indigo-50', border: 'border-indigo-100', accent: 'bg-indigo-500',
    labelBg: 'bg-indigo-100', labelText: 'text-indigo-700',
    heading: 'How they compare',
  },
  outlier: {
    icon: '⚠️', iconComp: AlertTriangle,
    bg: 'bg-rose-50', border: 'border-rose-100', accent: 'bg-rose-500',
    labelBg: 'bg-rose-100', labelText: 'text-rose-700',
    heading: 'Something stands out',
  },
  improvement: {
    icon: '🔧', iconComp: BarChart3,
    bg: 'bg-cyan-50', border: 'border-cyan-100', accent: 'bg-cyan-500',
    labelBg: 'bg-cyan-100', labelText: 'text-cyan-700',
    heading: 'Worth improving',
  },
};

const InsightItem = ({ insight }) => {
  const cfg = INSIGHT_CONFIG[insight.type] || INSIGHT_CONFIG.summary;
  const cleanText = stripTypePrefix(insight.text);

  return (
    <div className={`relative rounded-2xl border overflow-hidden ${cfg.bg} ${cfg.border}`}>
      {/* Coloured left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${cfg.accent}`} />

      <div className="pl-5 pr-4 py-4">
        {/* Heading badge */}
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full mb-2.5 ${cfg.labelBg} ${cfg.labelText}`}>
          <span>{cfg.icon}</span>
          {cfg.heading}
        </span>

        {/* Main text — larger, readable */}
        <p className="text-[15px] text-gray-800 leading-relaxed font-normal">
          {cleanText}
        </p>
      </div>
    </div>
  );
};

const renderInsights = (insights) => {
  if (!insights || !Array.isArray(insights) || insights.length === 0) return null;
  const valid = insights.filter(i => i && typeof i === 'object' && i.text);
  if (valid.length === 0) return null;

  return (
    <div className="mt-5 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4" style={{ color: COLORS.primary }} />
        <span className="text-sm font-semibold text-gray-600">What the data tells us</span>
      </div>
      {valid.map((insight, idx) => (
        <InsightItem key={idx} insight={insight} />
      ))}
    </div>
  );
};

// ─── Single-row detail card ────────────────────────────────────────────────

const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  // Numeric: format with commas but avoid inflating PO-number-like integers
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || Math.abs(value) > 1e9) return value.toLocaleString();
    // Looks like a PO / ID number — return as-is
    return String(value);
  }
  // Date strings
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime()) && /\d{4}/.test(value)) {
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }
  return String(value);
};

const isMoneyField = (key) => /price|amount|cost|spend|total|value/i.test(key);
const isIdField    = (key, val) => /number|id|code/i.test(key) && typeof val === 'number';

const RecordDetail = ({ row, insights }) => {
  const entries = Object.entries(row);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Details</span>
      </div>

      {/* Field grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {entries.map(([key, val]) => {
          const money  = isMoneyField(key);
          const idLike = isIdField(key, val);
          const displayVal = formatDetailValue(val);
          return (
            <div
              key={key}
              className="bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm flex flex-col gap-0.5"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{key}</span>
              <span
                className={`text-[15px] font-semibold leading-snug break-words
                  ${money  ? 'text-green-700' : ''}
                  ${idLike ? 'text-blue-600 font-mono' : ''}
                  ${!money && !idLike ? 'text-gray-800' : ''}`}
              >
                {money && typeof val === 'number' ? `₹${Number(val).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : displayVal}
              </span>
            </div>
          );
        })}
      </div>

      {insights && renderInsights(insights)}
    </div>
  );
};

// Helper function to transform API results to chart data
const transformToChartData = (results, metadata) => {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return [];
  }

  // If results are already in chart format (array of objects), return as is
  if (results[0] && typeof results[0] === 'object' && !Array.isArray(results[0])) {
    // Ensure all rows are valid objects
    return results
      .filter(row => row !== null && row !== undefined && typeof row === 'object')
      .map((row, idx) => {
        // Clean up the row - ensure all values are properly formatted
        const cleaned = {};
        Object.keys(row).forEach(key => {
          const value = row[key];
          if (value === null || value === undefined) {
            cleaned[key] = null;
          } else if (typeof value === 'number') {
            cleaned[key] = value;
          } else if (typeof value === 'string') {
            // Try to parse as number if it looks numeric
            const num = Number(value);
            cleaned[key] = !isNaN(num) && value.trim() !== '' ? num : value;
          } else {
            cleaned[key] = value;
          }
        });
        return cleaned;
      });
  }

  // If results are simple values, wrap them
  return results.map((row, idx) => {
    if (typeof row === 'object' && row !== null) {
      return row;
    }
    // Fallback for simple values
    return { 
      value: row, 
      index: idx,
      name: `Item ${idx + 1}`,
    };
  });
};

// Helper function to transform results to KPI cards
const transformToKPICards = (results, metadata) => {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return [];
  }

  // Ensure all results are valid objects
  const validResults = results.filter(r => r !== null && r !== undefined && typeof r === 'object');
  if (validResults.length === 0) {
    return [];
  }

  const firstRow = validResults[0];
  const keys = Object.keys(firstRow);

  if (keys.length === 0) {
    return [];
  }

  // Special handling: If single record, show ALL fields as cards for complete details
  if (validResults.length === 1) {
    const row = validResults[0];
    const colorPalette = CHART_COLORS;
    
    return keys.map((key, idx) => {
      const value = row[key];
      let displayValue = 'N/A';
      let isNumeric = false;
      let isUrl = false;
      
      if (value !== null && value !== undefined) {
        // Check if column name suggests it's a URL column
        const urlColumnNames = ['url', 'link', 'href', 'uri', 'file', 'attachment', 'document'];
        const isUrlColumn = urlColumnNames.some(name => key.toLowerCase().includes(name));
        
        // Check if value is a URL
        const extractedUrl = isValidUrl(value) ? value : (typeof value === 'string' ? extractUrl(value) : null);
        
        if (extractedUrl || (isUrlColumn && typeof value === 'string' && value.trim().length > 0)) {
          // If it's a URL column or contains a valid URL, preserve the URL value
          displayValue = extractedUrl || value;
          isUrl = true;
        } else if (typeof value === 'number') {
          displayValue = value.toLocaleString();
          isNumeric = true;
        } else if (typeof value === 'boolean') {
          displayValue = value ? 'Yes' : 'No';
        } else {
          const str = String(value);
          // Try to parse as number
          const num = Number(str);
          if (!isNaN(num) && isFinite(num) && str.trim() !== '' && str === String(num)) {
            displayValue = num.toLocaleString();
            isNumeric = true;
          } else {
            // Keep full value, truncation will happen in display
            displayValue = str;
          }
        }
      }
      
      return {
        title: key,
        value: displayValue,
        change: isNumeric ? 'Value' : (isUrl ? 'Link' : 'Detail'),
        trend: 'up',
        description: `${key} information`,
        color: colorPalette[idx % colorPalette.length],
        lightBg: idx % 2 === 0 ? 'bg-blue-50' : 'bg-purple-50',
        icon: Activity,
      };
    });
  }

  // Detect numeric columns dynamically for multiple rows
  const numericKeys = keys.filter(key => {
    const value = firstRow[key];
    if (value === null || value === undefined) return false;
    return typeof value === 'number' || 
           (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '');
  });

  // If we have metadata with column info, use it
  if (metadata && metadata.columns && Array.isArray(metadata.columns)) {
    const metadataNumericCols = metadata.columns.filter(col => col.type === 'number');
    if (metadataNumericCols.length > 0) {
      if (validResults.length === 1) {
        // Single row - create one card per numeric column
        const row = validResults[0];
        return metadataNumericCols.map((col, idx) => {
          const value = row[col.name];
          const numValue = typeof value === 'number' ? value : Number(value);
          const displayValue = !isNaN(numValue) ? numValue.toLocaleString() : String(value || 'N/A');
          
          return {
            title: col.name,
            value: displayValue,
            change: '+0%',
            trend: 'up',
            description: `${col.name} metric`,
            color: COLORS.primary,
            lightBg: 'bg-blue-50',
            icon: Activity,
          };
        });
      } else {
        // Multiple rows - aggregate
        return metadataNumericCols.map((col, idx) => {
          const values = validResults
            .map(r => {
              const val = r[col.name];
              return typeof val === 'number' ? val : Number(val);
            })
            .filter(v => !isNaN(v) && isFinite(v));
          
          const sum = values.reduce((a, b) => a + b, 0);
          const avg = values.length > 0 ? sum / values.length : 0;
          const count = values.length;
          
          return {
            title: col.name,
            value: avg.toLocaleString(),
            change: `${count} items`,
            trend: 'up',
            description: `Average ${col.name}`,
            color: COLORS.primary,
            lightBg: 'bg-blue-50',
            icon: Activity,
          };
        });
      }
    }
  }

  // Fallback: use detected numeric keys
  if (numericKeys.length > 0) {
    if (validResults.length === 1) {
      // Single row
      const row = validResults[0];
      return numericKeys.map((key, idx) => {
        const value = row[key];
        const numValue = typeof value === 'number' ? value : Number(value);
        const displayValue = !isNaN(numValue) ? numValue.toLocaleString() : String(value || 'N/A');
        
        return {
          title: key,
          value: displayValue,
          change: '+0%',
          trend: 'up',
          description: `${key} metric`,
          color: COLORS.primary,
          lightBg: 'bg-blue-50',
          icon: Activity,
        };
      });
    } else {
      // Multiple rows - aggregate
      return numericKeys.map((key, idx) => {
        const values = validResults
          .map(r => {
            const val = r[key];
            return typeof val === 'number' ? val : Number(val);
          })
          .filter(v => !isNaN(v) && isFinite(v));
        
        const sum = values.reduce((a, b) => a + b, 0);
        const avg = values.length > 0 ? sum / values.length : 0;
        const count = values.length;
        
        return {
          title: key,
          value: avg.toLocaleString(),
          change: `${count} items`,
          trend: 'up',
          description: `Average ${key}`,
          color: COLORS.primary,
          lightBg: 'bg-blue-50',
          icon: Activity,
        };
      });
    }
  }

  // Last resort: create cards from all columns
  return keys.slice(0, 5).map((key, idx) => {
    const value = firstRow[key];
    return {
      title: key,
      value: value !== null && value !== undefined ? String(value) : 'N/A',
      change: '+0%',
      trend: 'up',
      description: `${key} value`,
      color: COLORS.primary,
      lightBg: 'bg-blue-50',
      icon: Activity,
    };
  });
};

// Helper function to transform results to table format
const transformToTableData = (results) => {
  if (!results || !Array.isArray(results)) {
    return [];
  }
  // Return results as-is, ProductTable will handle dynamic columns
  return results.map((row, idx) => {
    // Ensure each row has an id for React keys
    if (row && typeof row === 'object') {
      return {
        id: row.id || idx,
        ...row,
      };
    }
    // If row is not an object, wrap it
    return {
      id: idx,
      value: row,
    };
  });
};

const MessageContent = ({ message, isUser, onSuggestionClick, isQueryLoading = false }) => {
  // Validate message
  if (!message || typeof message !== 'object') {
    return (
      <div className="text-sm text-gray-500">
        Invalid message format
      </div>
    );
  }

  if (message.type === 'text') {
    const content = message.content !== undefined && message.content !== null 
      ? String(message.content) 
      : '';
    
    return (
      <div>
        {content && (
          <p className={`text-sm leading-relaxed ${isUser ? 'text-white' : 'text-gray-700'}`}>
            {content}
          </p>
        )}
        {message.apiData?.insights && renderInsights(message.apiData.insights)}
        {message.apiData?.warnings && Array.isArray(message.apiData.warnings) && message.apiData.warnings.length > 0 && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            <strong>Warnings:</strong> {message.apiData.warnings.filter(w => w).join(', ')}
          </div>
        )}
      </div>
    );
  }

  if (message.type === 'cards') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const cards = transformToKPICards(results, metadata);

      if (cards.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No metric data available. Try asking for specific metrics or counts.
          </p>
        );
      }

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4" style={{ color: COLORS.primary }} />
              Details
            </h3>
            {apiData.metadata && typeof apiData.metadata.rowCount === 'number' && (
              <span className="text-xs text-gray-500">
                {apiData.metadata.rowCount} result(s)
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.map((card, idx) => (
              <KPICard key={idx} card={card} />
            ))}
          </div>
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering cards:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering metric cards: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'detail') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results
                    : Array.isArray(message.content)  ? message.content
                    : [];
      const row = results[0];
      if (!row || typeof row !== 'object') {
        return <p className="text-sm text-gray-600">No record found.</p>;
      }
      return <RecordDetail row={row} insights={apiData.insights} />;
    } catch (error) {
      console.error('Error rendering detail card:', error);
      return <div className="text-sm text-red-600">Error rendering record: {error instanceof Error ? error.message : 'Unknown error'}</div>;
    }
  }

  if (message.type === 'line-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for line chart visualization.
          </p>
        );
      }

      return (
        <div>
          <AreaChart data={chartData} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering line chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering line chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'bar-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for bar chart visualization.
          </p>
        );
      }

      return (
        <div>
          <BarChart data={chartData} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering bar chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering bar chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'pie-chart' || message.type === 'donut-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for {message.type === 'donut-chart' ? 'donut' : 'pie'} chart visualization.
          </p>
        );
      }

      return (
        <div>
          <PieChart data={chartData} isDonut={message.type === 'donut-chart'} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering pie/donut chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering {message.type === 'donut-chart' ? 'donut' : 'pie'} chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'area-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for area chart visualization.
          </p>
        );
      }

      return (
        <div>
          <AreaChart data={chartData} isArea={true} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering area chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering area chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'table') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const tableData = transformToTableData(results);

      if (tableData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for table visualization.
          </p>
        );
      }

      return (
        <div>
          <ProductTable data={tableData} apiData={apiData} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering table:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering table: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'suggestions') {
    return (
      <Suggestions 
        suggestions={message.content} 
        onSuggestionClick={onSuggestionClick}
        isLoading={false}
      />
    );
  }

  if (message.type === 'followup-suggestions') {
    return (
      <FollowupSuggestions 
        suggestions={message.content} 
        onSuggestionClick={onSuggestionClick}
        disabled={isQueryLoading}
      />
    );
  }

  return null;
};

export default MessageContent;

